import { useMutation } from '@apollo/client';
import {} from '@clearkit/react';
import CircularProgress from '@material-ui/core/CircularProgress';
import map from 'lodash/map';

import { ConfirmDialogBase } from '~/components/ConfirmDialog';
import Select from '~/components/Select';
import TextField from '~/components/TextField';
import history from '~/lib/history';
import paths from '~/lib/paths';
import createCSVSegmentQuery from '~/pages/CsvImport/createSegment.graphql';

import styles from './styles';

const collectionNodesToOptions = (collections) => {
  if (!collections) return [];

  return map(map(collections.edges, 'node'), ({ id, name }) => ({
    value: id,
    label: name,
  }));
};

const CreateSegmentDialog = ({
  classes,
  csvImport,
  closeDialog,
  collections,
  type,
}) => {
  const [segmentName, setSegmentName] = useState('');
  const [collection, setCollection] = useState(undefined);
  const [validations, setValidations] = useState({});

  const clearStateAndClose = () => {
    setSegmentName('');
    setCollection(undefined);
    setValidations({});
    closeDialog();
  };

  const handleCSVSegmentCreateComplete = ({ response: { node } }) => {
    clearStateAndClose();
    history.push(paths.segment(type, node.segmentId));
  };

  const [createCSVSegment, { loading: isCreatingSegment }] = useMutation(
    createCSVSegmentQuery,
    {
      onCompleted: handleCSVSegmentCreateComplete,
    },
  );

  useEffect(() => {
    if (csvImport) {
      setSegmentName(csvImport?.sourceName);
    }
  }, [csvImport]);

  const validate = () => ({
    collection: !collection,
    segmentName: !segmentName,
  });

  const handleCreateSegment = async () => {
    const newValidations = validate();

    if (Object.values(newValidations).some((value) => value)) {
      setValidations(newValidations);
      return;
    }

    createCSVSegment({
      variables: {
        input: {
          id: csvImport.id,
          segmentName,
          collectionId: collection.value,
        },
      },
    });
  };

  return (
    <ConfirmDialogBase
      maxWidth="md"
      classes={{ paper: classes.dialogPaper }}
      contentClasses={{ root: classes.dialogContentRoot }}
      confirmButtonVariant={'blue'}
      confirmText={
        isCreatingSegment ? (
          <CircularProgress className={classes.circularProgress} size={16} />
        ) : (
          'Create audience'
        )
      }
      buttonDisabled={isCreatingSegment}
      message={
        <>
          <div className={classes.fieldLabel}>Audience name</div>
          <TextField
            disabled={isCreatingSegment}
            error={!!validations.segmentName}
            value={segmentName}
            fullWidth
            onChange={(e) => {
              const { segmentName: _, ...newValidations } = validations;
              setValidations(newValidations);
              setSegmentName(e.target.value);
            }}
          />

          <div
            className={classnames(
              classes.fieldLabel,
              classes.collectionSelectLabel,
            )}>
            Collection
          </div>
          <div rel="select-collection">
            <Select
              isDisabled={isCreatingSegment}
              error={!!validations.collection}
              onChange={(option) => {
                const { collection: _, ...newValidations } = validations;
                setValidations(newValidations);
                setCollection(option);
              }}
              options={collectionNodesToOptions(collections)}
            />
          </div>
        </>
      }
      onCancel={clearStateAndClose}
      onConfirm={handleCreateSegment}
      open={!!csvImport}
      title={`Create audience from ${csvImport?.sourceName}`}
    />
  );
};

export default withStyles(styles)(CreateSegmentDialog);
